import DOMPurify from 'dompurify';
import type {DOMPurify as DOMPurifyInstance, UponSanitizeAttributeHookEvent} from 'dompurify';
import {getOurMediaUrl} from '@Libraries/chat-media-url.library';
import {
  FETCHING_ATTRIBUTES,
  FETCHING_STYLE_PATTERN,
  FORBIDDEN_TAGS,
  IMAGE_TAG_NAME,
  LAZY_LOAD_CLASSES,
  SUFFIX_ANCHOR_SELECTOR,
  SUFFIX_CONTAINER_CLASS,
  SUFFIX_CONTAINER_MARGIN_LEFT,
} from '@Components/ai-chat-window/components/markdown-renderer/markdown-renderer.types';

function buildSuffixContainer(): HTMLSpanElement {
  const container = document.createElement('span');
  container.className = SUFFIX_CONTAINER_CLASS;
  container.style.display = 'inline';
  container.style.marginLeft = SUFFIX_CONTAINER_MARGIN_LEFT;

  return container;
}

function removeStaleSuffixContainers(body: Element): void {
  body.querySelectorAll(`.${SUFFIX_CONTAINER_CLASS}`).forEach((stale) => {
    stale.remove();
  });
}

function lastAnchorInsideFinalBlock(body: Element): HTMLElement | null {
  const anchors = body.querySelectorAll<HTMLElement>(SUFFIX_ANCHOR_SELECTOR);
  const lastAnchor = anchors[anchors.length - 1] ?? null;

  if (!lastAnchor || !body.lastElementChild?.contains(lastAnchor)) {
    return null;
  }

  return lastAnchor;
}

export function attachSuffixContainer(body: Element): HTMLSpanElement {
  removeStaleSuffixContainers(body);

  const container = buildSuffixContainer();
  const anchor = lastAnchorInsideFinalBlock(body);

  if (anchor) {
    anchor.appendChild(container);

    return container;
  }

  body.appendChild(container);

  return container;
}

/**
 * Strips leading indentation from HTML-tag lines so pretty-printed HTML from the agent isn't parsed as a markdown code block.
 * Only lines whose first non-space character is `<` are de-indented, leaving markdown lists and prose untouched.
 */
export function deindentHtmlLines(content: string): string {
  return content.replace(/^[ \t]+(?=<)/gm, '');
}

let chatMarkdownPurifier: DOMPurifyInstance | null = null;

/** Besides stripping script, keeps the page from fetching anything but our own media without a click. */
export function sanitizeChatMarkdownHtml(rawHtml: string): string {
  return getChatMarkdownPurifier().sanitize(rawHtml, {
    USE_PROFILES: {html: true},
    FORBID_TAGS: [...FORBIDDEN_TAGS],
    ALLOW_DATA_ATTR: false,
  });
}

/** Its own instance, so the hook never applies to the app's other sanitize calls. */
function getChatMarkdownPurifier(): DOMPurifyInstance {
  if (chatMarkdownPurifier === null) {
    chatMarkdownPurifier = DOMPurify(window);
    chatMarkdownPurifier.addHook('uponSanitizeAttribute', sanitizeChatMarkdownAttribute);
  }

  return chatMarkdownPurifier;
}

function sanitizeChatMarkdownAttribute(node: Element, data: UponSanitizeAttributeHookEvent): void {
  const attributeName = data.attrName.toLowerCase();

  if (isFetchingAttribute(attributeName, data.attrValue)) {
    data.keepAttr = false;
    return;
  }

  if (attributeName === 'class') {
    data.attrValue = withoutLazyLoadClasses(data.attrValue);
    return;
  }

  if (attributeName === 'src') {
    keepOnlyOurImageSource(node, data);
  }
}

function isFetchingAttribute(attributeName: string, attributeValue: string): boolean {
  return FETCHING_ATTRIBUTES.includes(attributeName) || (attributeName === 'style' && FETCHING_STYLE_PATTERN.test(attributeValue));
}

function keepOnlyOurImageSource(node: Element, data: UponSanitizeAttributeHookEvent): void {
  const ourMediaUrl = node.nodeName === IMAGE_TAG_NAME ? getOurMediaUrl(data.attrValue) : null;
  if (ourMediaUrl === null) {
    data.keepAttr = false;
    return;
  }

  data.attrValue = ourMediaUrl;
}

function withoutLazyLoadClasses(classList: string): string {
  return classList
    .split(/\s+/)
    .filter((className) => className !== '' && !LAZY_LOAD_CLASSES.includes(className))
    .join(' ');
}
